import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useBuyerLists } from '@/hooks/useBuyerLists';
import { useToast } from '@/hooks/use-toast';
import { Plus } from 'lucide-react';

interface AddToListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buyerId: string;
  buyerName: string;
}

export const AddToListDialog = ({ open, onOpenChange, buyerId, buyerName }: AddToListDialogProps) => {
  const [selectedListId, setSelectedListId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [showNewListForm, setShowNewListForm] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { lists, isLoading, createList, addBuyerToList } = useBuyerLists();
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!selectedListId && !showNewListForm) {
      toast({
        title: "Error",
        description: "Please select a list or create a new one",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      let listId = selectedListId;
      
      if (showNewListForm) {
        if (!newListName.trim()) {
          toast({
            title: "Error",
            description: "Please enter a list name",
            variant: "destructive"
          });
          setIsSubmitting(false);
          return;
        }
        
        const result = await createList(newListName.trim(), newListDescription.trim() || undefined);
        
        if (!result?.success || !result?.data) {
          toast({
            title: "Error",
            description: "Failed to create list",
            variant: "destructive"
          });
          setIsSubmitting(false);
          return;
        }
        
        const newList = result.data;
        
        listId = newList.id;
      }
      
      const success = await addBuyerToList(listId, buyerId, notes.trim() || undefined);
      
      if (success) {
        toast({
          title: "Success",
          description: `${buyerName} has been added to the list`,
        });
        onOpenChange(false);
        resetForm();
      } else {
        toast({
          title: "Error",
          description: "Failed to add buyer to list",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error adding buyer to list:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedListId('');
    setNotes('');
    setShowNewListForm(false);
    setNewListName('');
    setNewListDescription('');
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add {buyerName} to List</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {!showNewListForm ? (
            <div className="space-y-2">
              <Label>Select List</Label>
              <Select value={selectedListId} onValueChange={setSelectedListId} disabled={isLoading}>
                <SelectTrigger>
                  <SelectValue placeholder={isLoading ? "Loading lists..." : "Choose a list"} />
                </SelectTrigger>
                <SelectContent>
                  {lists.map((list) => (
                    <SelectItem key={list.id} value={list.id}>
                      {list.list_name} ({list.buyer_count || 0} buyers)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNewListForm(true)}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create New List
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>List Name</Label>
                <Input
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="Enter list name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Textarea
                  value={newListDescription}
                  onChange={(e) => setNewListDescription(e.target.value)}
                  placeholder="Enter list description"
                  rows={3}
                />
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNewListForm(false)}
                className="w-full"
              >
                Back to Existing Lists
              </Button>
            </div>
          )}
          
          <div className="space-y-2">
            <Label>Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this buyer..."
              rows={3}
            />
          </div>
          
          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting} className="flex-1">
              {isSubmitting ? "Adding..." : "Add to List"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};